import React, { useEffect } from 'react';

/**
 *
 * @module KeyboardListener
 *
 * @description Keyboard listener for scene rotation
 *
 * @param {string} sceneId - The id of the scene. Used for adding and removing event listeners.
 * @param {object} controlsRef - The ref of the `OrbitControls` instance.
 * @param {boolean} isFlatScene - Whether the scene is flat or not. Defaults to `false`.
 * @param {string} modifierKey - The key that needs to be pressed for the keyboard controls to work. Defaults to `shiftKey`.
 * @param rotationAngle - The angle by which the scene should rotate on each key press. Defaults to `0.2`.
 * @returns {null}
 *
 * @example
 * <KeyboardListener
 *    sceneId={sceneId}
 *        controlsRef={controlsRef}
 *        isFlatScene={isFlatScene}
 *        />
 *
 * @description
 *    This component can be used for enabling keyboard controls for scene rotation.
 *        - It listens to the arrow keys and updates the controlsRef accordingly.
 *        - It only works if the `modifierKey` key is pressed.
 *        - It only works if the scene is not flat.
 *        - It is used in the Scene component currently.
 *        - Uses `rotateUp` and `rotateLeft` methods of `OrbitControls` to rotate the scene.
 *        - Currently only supports ArrowUp, ArrowDown, ArrowLeft, ArrowRight keys for rotation.
 *
 * */
const KeyboardListener = ({
	sceneId,
	controlsRef,
	isFlatScene = false,
	modifierKey = 'shiftKey',
	rotationAngle = 0.2,
}) => {
	const onArrowKeysForSceneRotation = (e) => {
		if (e?.[modifierKey] === false || isFlatScene) return;

		e.stopPropagation();

		switch (e.key) {
			case 'ArrowUp':
				controlsRef.current.rotateUp(rotationAngle);
				break;
			case 'ArrowDown':
				controlsRef.current.rotateUp(-rotationAngle);
				break;
			case 'ArrowLeft':
				controlsRef.current.rotateLeft(rotationAngle);
				break;
			case 'ArrowRight':
				controlsRef.current.rotateLeft(-rotationAngle);
				break;
			default:
				return;
		}

	};

	const addThreeEditorKeyboardEvents = () => {
		document.addEventListener('keydown', onArrowKeysForSceneRotation);
	};

	const removeThreeEditorKeyboardEvents = () => {
		document.removeEventListener('keydown', onArrowKeysForSceneRotation);
	};

	useEffect(() => {
		addThreeEditorKeyboardEvents();
		return () => {
			removeThreeEditorKeyboardEvents();
		};
	}, [sceneId]);

	return null;
};

export default KeyboardListener;
